// MenuPrices - in-store list price lookup for phone orders (from the Toast menu).
// Not what a customer paid: see Live Orders for that.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const api = axios.create({ baseURL: API_URL, withCredentials: true });
const money = (v) => (v == null ? '' : `$${Number(v).toFixed(2)}`);

export default function MenuPrices() {
  const [q, setQ] = useState('');
  const [menu, setMenu] = useState({ items: [], modifiers: [], builtAt: null, error: null });
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/alerts/menu-prices').then(r => setMenu(r.data)).catch(e => setMenu(m => ({ ...m, error: e.message })));
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const results = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const all = [...menu.items, ...menu.modifiers];
    if (words.length === 0) return menu.items;
    return all.filter(it => {
      const hay = `${it.name} ${it.group}`.toLowerCase();
      return words.every(w => hay.includes(w));
    }).slice(0, 60);
  }, [q, menu]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const it of results) {
      const key = it.groupPath || it.group;
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(it);
    }
    return [...g.entries()];
  }, [results]);

  return (
    <div className="container mx-auto px-4 pb-8 max-w-3xl">
      <h2 className="text-xl font-bold mb-2">Menu Prices <span className="text-sm font-normal text-gray-400">(in-store list price for phone orders)</span></h2>
      <input
        ref={inputRef}
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Type an item… coke, chips, tres leches, steak"
        className="w-full text-2xl px-4 py-3 rounded-lg text-black mb-3"
      />
      {menu.error && <div className="text-red-400 text-sm mb-2">Menu not loaded: {menu.error}</div>}
      <div className="bg-gray-800 rounded-xl p-4">
        {grouped.map(([group, items]) => (
          <div key={group} className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">{group}</div>
            {items.map(it => (
              <div key={`${it.kind}-${it.guid || it.name}`} className="flex justify-between items-baseline py-1 border-b border-gray-700/50">
                <span className={`${q ? 'text-xl' : 'text-base'} text-white`}>{it.name}{it.kind === 'modifier' ? <span className="text-gray-500 text-xs ml-2">add-on</span> : null}</span>
                <span className={`${q ? 'text-3xl' : 'text-xl'} font-black text-green-300 font-mono ml-3 whitespace-nowrap`}>{money(it.price)}</span>
              </div>
            ))}
          </div>
        ))}
        {q && results.length === 0 && <div className="text-gray-400">Nothing on the menu matches “{q}”.</div>}
      </div>
      <div className="text-xs text-gray-500 mt-2">
        Prices from Toast in-store menu{menu.builtAt ? `, refreshed ${new Date(menu.builtAt).toLocaleTimeString()}` : ''}. Delivery apps charge more.
      </div>
    </div>
  );
}
